import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../api";
import ProductCard from "../components/ProductCard";
import SEO, { buildBreadcrumbJsonLd } from "../components/SEO";

/**
 * Category landing page — /category/{slug}
 * Purpose: give each product category a dedicated indexable URL so we can
 * rank for category-level search intent ("peptide nasal spray", "GLP-1
 * peptides", "healing peptides") rather than only individual PDPs.
 *
 * Renders:
 *  - SEO tags with category-specific title, description, canonical URL
 *  - Breadcrumb + CollectionPage + ItemList JSON-LD for rich results
 *  - H1 with the category name
 *  - Category-specific intro paragraph
 *  - Product grid filtered to just this category (reuses ProductCard)
 *  - Cross-nav back to /shop
 *
 * The URL slug is derived from category name via
 *   category.toLowerCase().replace(/\s+/g, '-')
 * — this MUST match the sitemap generator's slugification (see server.py).
 */

// Human-friendly category intros. Keep short (2-3 sentences), benefit-first,
// keyword-forward for SEO, compliant (research-only framing). Every live
// category from PRODUCT_SEED gets an entry here; the fallback below covers
// any category added later via the admin dashboard.
const CATEGORY_META = {
  "prosource-mist": {
    displayName: "ProSource Mist — Peptide Nasal Sprays",
    intro:
      "ProSource Mist is our ready-to-use nasal spray line. Every bottle is pre-mixed, third-party tested, and delivered through a metered pump — no reconstitution, no syringes, no mixing. Ideal for research protocols that call for multiple daily administrations.",
    keywords: "peptide nasal spray, Selank nasal spray, Semax nasal spray, MOTS-c nasal spray, NAD+ nasal spray, ready-to-use peptide, intranasal peptide research",
  },
  "skincare": {
    displayName: "Skincare — Multi-Peptide Serums",
    intro:
      "Professional-strength cosmetic peptide skincare from ProSource Labs. Featuring Blue Renewal™ multi-peptide facial serum with 2% GHK-Cu (Copper Tripeptide-1) and 3% SNAP-8 (Acetyl Octapeptide-3), formulated to help improve the appearance of smoother, firmer, healthier-looking skin — softening the look of fine lines and wrinkles while supporting hydration, skin brightening, and skin tightening. Made in USA.",
    keywords: "copper peptide serum, GHK-Cu serum, SNAP-8 serum, multi-peptide facial serum, face serum, face cream, wrinkle serum, fine line serum, skin brightening serum, skin tightening serum, skin firming serum, anti-aging peptide serum, hyaluronic acid serum, niacinamide serum, professional strength skincare, Blue Renewal, ProSource Labs skincare, made in USA skincare",
  },
  "glp-1-research": {
    displayName: "GLP-1 Research Peptides",
    intro:
      "The GLP-1 research category covers Retatrutide, Tirzepatide, and Semaglutide — the most-studied peptides in modern metabolic and weight-management research. Every SKU is HPLC verified, US-shipped, and available at tiered volume pricing.",
    keywords: "GLP-1 research peptides, Retatrutide, Tirzepatide, Semaglutide, buy GLP-1 peptides, metabolic research peptides",
  },
  "growth-hormone-secretagogue": {
    displayName: "Growth Hormone Secretagogues",
    intro:
      "Growth hormone secretagogues — Tesamorelin, Ipamorelin, CJC-1295 (with and without DAC), GHRP-6, GHRP-2 — are the research market's core toolkit for GH-axis studies. All third-party HPLC verified, tiered volume pricing available.",
    keywords: "growth hormone secretagogue, Tesamorelin, Ipamorelin, CJC-1295, GHRP-6, GHRP-2, GH research peptides",
  },
  "metabolic-research": {
    displayName: "Metabolic & Mitochondrial Research",
    intro:
      "Metabolic and mitochondrial research peptides — MOTS-c, AICAR, SLU-PP-332 — sit at the frontier of cellular-energy and endurance-research literature. Verified for identity and purity by third-party HPLC.",
    keywords: "MOTS-c, AICAR, SLU-PP-332, metabolic research peptides, mitochondrial peptides, exercise mimetic research",
  },
  "longevity-research": {
    displayName: "Longevity & NAD+ Research",
    intro:
      "The longevity category features NAD+ — the cellular coenzyme central to modern healthy-aging, DNA-repair, and sirtuin-activation research. HPLC verified, fast US shipping.",
    keywords: "NAD+, longevity peptides, healthy aging research, sirtuin activation, DNA repair research",
  },
  "growth-factor": {
    displayName: "Growth Factor Research",
    intro:
      "Growth-factor research SKUs cover IGF-1 LR3 and related long-acting analogs — central research tools in muscle-protein synthesis, tissue-repair, and IGF-1-axis studies.",
    keywords: "IGF-1 LR3, growth factor research, muscle research peptides, IGF-1 axis",
  },
  "healing-recovery": {
    displayName: "Healing & Recovery Peptides",
    intro:
      "Healing and recovery peptides — BPC-157, TB-500, GHK-Cu, and the classic BPC/TB \"Wolverine\" stack — anchor the tissue-repair research literature. Third-party HPLC verified, tiered pricing.",
    keywords: "BPC-157, TB-500, GHK-Cu, healing peptides, Wolverine stack, tissue repair research",
  },
  "specialty-peptides": {
    displayName: "Specialty & Neuropeptide Research",
    intro:
      "Specialty research peptides — PT-141, Melanotan-2, KPV, Kisspeptin-10, SNAP-8, DSIP, Glutathione, AOD-9604 — cover the melanocortin, cognition, sleep, and skin-research literature. All lab-verified.",
    keywords: "PT-141, Melanotan-2, KPV, Kisspeptin-10, SNAP-8, DSIP, Glutathione, AOD-9604, specialty research peptides",
  },
  "research-blend": {
    displayName: "Research Blends (GLOW & KLOW)",
    intro:
      "Research blends combine multiple active peptides into a single vial. GLOW and KLOW are our two flagship dermal-research blends. Composition printed on each batch card.",
    keywords: "GLOW peptide blend, KLOW peptide blend, research peptide blends, skin research peptides",
  },
  "recombinant": {
    displayName: "Recombinant Proteins",
    intro:
      "Recombinant protein research SKUs — including full-length HGH-191AA — are handled and characterized differently from short synthetic peptide fragments. Batch-tested, US-shipped.",
    keywords: "HGH-191AA, recombinant human growth hormone, recombinant proteins",
  },
  "lab-supplies": {
    displayName: "Lab Supplies",
    intro:
      "Bacteriostatic water, acetic acid solvent, alcohol prep pads — the essential supplies every research bench needs to work with reconstituted peptides.",
    keywords: "bacteriostatic water, acetic acid, alcohol prep pads, peptide reconstitution supplies, lab supplies",
  },
};

function slugToCategory(slug, categories) {
  // Reverse-lookup: find the original category name whose slug matches.
  const target = (slug || "").toLowerCase();
  return categories.find((c) => c.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") === target);
}

export default function CategoryPage() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => setProducts(data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoaded(true));
  }, []);

  const allCategories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category))).filter(Boolean),
    [products]
  );

  const matchedCategory = slugToCategory(slug, allCategories);
  const filtered = useMemo(
    () => products.filter((p) => p.category === matchedCategory),
    [products, matchedCategory]
  );

  const meta = CATEGORY_META[slug] || {
    displayName: matchedCategory || "Research Compounds",
    intro: matchedCategory
      ? `Browse every ${matchedCategory} SKU in the ProSource Labs catalog. Third-party HPLC verified for identity and purity, fast US domestic shipping, tiered volume pricing on every product.`
      : "Browse research-grade peptides from ProSource Labs. Third-party HPLC verified, US-shipped.",
    keywords: `${matchedCategory || ""} research peptides, buy peptides, lab verified peptides`,
  };

  // If products have finished loading and this slug doesn't match any live
  // category, redirect to /shop rather than serving a soft-404 with no content.
  if (loaded && !matchedCategory) {
    return <Navigate to="/shop" replace />;
  }

  const canonical = `/category/${slug}`;
  const seoTitle = `${meta.displayName} · Research-Grade`;
  const seoDescription = `${meta.intro} ${filtered.length ? `${filtered.length} products available.` : ""}`.trim();

  // CollectionPage + ItemList JSON-LD for rich results on category pages.
  const itemList = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: meta.displayName,
    description: meta.intro,
    url: `https://prosourcelabs.org${canonical}`,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: filtered.length,
      itemListElement: filtered.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://prosourcelabs.org/product/${p.slug}`,
        name: p.name,
      })),
    },
  };

  const breadcrumb = buildBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Shop", path: "/shop" },
    { name: meta.displayName, path: canonical },
  ]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid={`category-page-${slug}`}>
      <SEO
        title={seoTitle}
        titleTemplate="product"
        description={seoDescription}
        path={canonical}
        keywords={meta.keywords}
        jsonLd={[itemList, breadcrumb]}
      />

      <nav className="text-[10px] font-mono-tech tracking-[0.2em] uppercase text-[#5C657A] mb-6">
        <Link to="/" className="hover:text-white transition-colors">Home</Link>
        <span className="mx-2">·</span>
        <Link to="/shop" className="hover:text-white transition-colors">Shop</Link>
        <span className="mx-2">·</span>
        <span className="text-[#00C3FF]">{meta.displayName}</span>
      </nav>

      <div className="overline">CATEGORY</div>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-3 font-light" data-testid="category-h1">
        {meta.displayName}
      </h1>
      <p className="text-base text-[#8A94A6] mt-5 max-w-2xl leading-relaxed">
        {meta.intro}
      </p>
      <p className="text-xs text-[#5C657A] mt-3 font-mono-tech uppercase tracking-[0.2em]">
        {filtered.length} product{filtered.length === 1 ? "" : "s"} · Third-party HPLC verified · Ships from Southern California
      </p>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 mt-12">
          {filtered.map((p) => (
            <ProductCard key={p.id || p.slug} product={p} />
          ))}
        </div>
      ) : (
        <div className="mt-16 text-sm text-[#8A94A6]">
          {loaded ? "No products in this category yet." : "Loading catalog..."}
        </div>
      )}

      <div className="mt-16 border-t border-white/10 pt-8">
        <Link
          to="/shop"
          data-testid="category-view-full-catalog-link"
          className="inline-flex items-center gap-2 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#00C3FF] hover:text-white transition-colors"
        >
          View full catalog <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
